import { useEffect, useRef, useState } from 'react';
import { Check, Copy } from 'lucide-react';

import { SoftCard } from '@/components/ui/soft-card';
import { copyToClipboard } from '@/lib/utils';

// Channels for which `feeds/generate-feed.js` actually produces and persists a
// `feeds.near.st/{shopId}/{type}.xml` URL onto `channel.config.url`. Sourced
// from `channels/services/feeds/serverless.yml` (the `generate-feed` SNS
// filter) — keep this list in sync with that handler. Other `feedExport: true`
// channels in `@nearst/channel-definitions` (e.g. `just-eat`) do NOT get a
// `feeds.near.st` URL, so this card must not render for them.
const FEED_URL_CHANNEL_IDS = new Set(['pinterest-feed', 'meta-commerce-feed', 'tiktok-shop-feed', 'snap-store-feed']);

export function FeedUrlCard({ channel }) {
	const [copied, setCopied] = useState(false);
	const copiedTimerRef = useRef(null);

	useEffect(() => () => clearTimeout(copiedTimerRef.current), []);

	if (!channel || !FEED_URL_CHANNEL_IDS.has(channel.id)) return null;

	const url = channel.record?.config?.url ?? null;

	const handleCopy = async () => {
		if (!url) return;
		if (await copyToClipboard(url)) {
			setCopied(true);
			clearTimeout(copiedTimerRef.current);
			copiedTimerRef.current = setTimeout(() => setCopied(false), 1800);
		}
	};

	return (
		<SoftCard className="space-y-3 !p-5 md:!p-6">
			<div>
				<h3 className="text-[15px] font-bold text-foreground md:text-base">Feed URL</h3>
				<p className="mt-0.5 text-xs text-muted-foreground">Paste this into {channel.title} to connect your product catalogue.</p>
			</div>

			{url ? (
				<div className="flex flex-wrap items-center gap-2">
					<code className="min-w-0 flex-1 truncate rounded-md border border-border bg-muted px-3 py-2 font-mono text-xs text-foreground">
						{url}
					</code>
					<button
						type="button"
						onClick={handleCopy}
						className="inline-flex items-center justify-center gap-2 rounded-full bg-muted px-4 py-2 text-sm font-semibold text-foreground transition hover:bg-accent"
					>
						{copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
						{copied ? 'Copied' : 'Copy'}
					</button>
				</div>
			) : (
				<div className="rounded-md border border-dashed border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
					Feed pending — usually ready within a few minutes of enabling.
				</div>
			)}
		</SoftCard>
	);
}
